import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { useGetParticipantMeParticipantsMeGet } from "@/api-v3";
import { CopyButton } from "@/components/copyable-id";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePaymentMethods } from "@/features/payments";
import { hasDisputeContact } from "@/features/payments/types/payment-account-cards";
import StorageManager from "@/features/storage";

import { RenderList } from "../components/render-list";
import { buildDisputeMailto } from "../helpers/dispute-mailto";

/**
 * How to raise a dispute or complaint about a Stripe card. The address is the
 * platform-wide `dispute_email` PASS attaches to every Stripe card, so the
 * first card carrying one is as good as any. Reached from the card actions.
 */
export const CardDisputePage = () => {
  const { t } = useTranslation();
  const { cards, paymentMethodsLoading } = usePaymentMethods();
  const { data: participant } = useGetParticipantMeParticipantsMeGet();

  const disputeEmail = cards.find(hasDisputeContact)?.dispute_email;

  if (paymentMethodsLoading) {
    return (
      <div className="bg-white p-4 h-full space-y-3">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-1/2" />
      </div>
    );
  }

  if (!disputeEmail) {
    return <Navigate to="/card/details" replace />;
  }

  const loginId =
    participant?.login_id ??
    StorageManager.getLoginInformation()?.loginId ??
    "";
  const mailto = buildDisputeMailto({
    email: disputeEmail,
    subject: t("card_disputeEmailSubject"),
    body: [
      `${t("payment_name")}: `,
      `${t("login_id")}: ${loginId}`,
      `${t("card_disputeIssueDescription")}: `,
    ].join("\n"),
  });

  return (
    <div className="bg-white p-4 h-full">
      <h6 className="text-xl font-semibold mb-4">
        {t("card_disputeOrComplaint")}
      </h6>
      <p className="mb-2">{t("card_disputeIntro")}</p>
      {/* Plain text with a one-tap copy: the fallback when no mail app is set up. */}
      <p className="mb-4 flex items-center gap-1 min-w-0">
        <span className="font-semibold break-all">{disputeEmail}</span>
        <CopyButton value={disputeEmail} label={t("card_copyEmail")} />
      </p>
      <p className="mb-2">{t("card_disputeIncludeInEmail")}</p>
      <RenderList
        items={["payment_name", "login_id", "card_disputeIssueDescription"]}
      />
      <Button asChild className="w-full mt-6">
        <a href={mailto}>{t("email_support")}</a>
      </Button>
    </div>
  );
};
